import React from 'react';
import clsx from 'clsx';
import { Circle } from '../../icons/circle';
import { CheckInCircle } from '../../icons/check-in-circle';
import { MinusInCircle } from '../../icons/minus-in-circle';
import { LineItemIcon, MediaPlanIcon } from '../layout/nav-icons';
import { calcProportion } from '../tables/cell-renderers/utils';
import { LineItemPlan } from 'shared/src/domain/line-item/line-item-types';
import {
  isLineItemDetailRow,
  TooltipProps as LineItemsTooltipProps
} from '../../features/line-items/all-line-items';
import { TooltipProps as CampaignsTooltipProps } from '../../features/zoho-campaigns/all-campaigns';
import { isNil } from 'lodash';
import { CampaignTableRow } from 'shared/src/api/campaigns';
import { countAllocatedLabel, pctAllocatedLabel } from '../tables/cell-renderers/amount-allocated';

export type Property = {
  label: string;
  percentage?: number;
  value?: string;
};

export function LineItemPlanStatusTooltip({ value }: LineItemsTooltipProps<Property[]>) {
  return value ? (
    <PlanStatusTooltip entityIcon={<MediaPlanIcon />} heading="Media Plans" properties={value} />
  ) : null;
}

export function CampaignLineItemsPlanStatusTooltip({ value }: LineItemsTooltipProps<Property[]>) {
  return value ? (
    <PlanStatusTooltip entityIcon={<LineItemIcon />} heading="Line Items" properties={value} />
  ) : null;
}

export function CampaignMediaPlansPlanStatusTooltip({ value }: LineItemsTooltipProps<Property[]>) {
  return value ? (
    <PlanStatusTooltip entityIcon={<MediaPlanIcon />} heading="Media Plans" properties={value} />
  ) : null;
}

export function lineItemPlanStatusTooltipValue(props: LineItemsTooltipProps<LineItemPlan>) {
  const { data } = props;
  if (!data || !isLineItemDetailRow(data)) return null;

  if (data.num_media_plans > 0) {
    return [{ label: 'Has media plans', percentage: 100 }];
  }
  return [{ label: 'No media plans' }];
}

type Props = {
  entityIcon: React.ReactNode;
  heading: string;
  properties: Property[];
};

export function PlanStatusTooltip({ entityIcon, heading, properties }: Props) {
  return (
    <div className="shadow-b-lg flex w-[300px] flex-col items-center gap-2 rounded-sm bg-white p-2">
      <div className="flex items-center gap-2.5">
        {entityIcon}
        <span className="text-base font-semibold text-gray-600">{heading}</span>
      </div>
      {properties.map(property => (
        <PropertyStatus key={property.label} {...property} />
      ))}
    </div>
  );
}

export function PropertyStatus({ label, percentage, value }: Property) {
  return (
    <div
      className={clsx(
        'flex w-full items-center justify-between rounded-sm px-2 py-0.5 text-xs',
        bgColor(percentage)
      )}>
      <div className="flex items-center gap-1 font-medium text-gray-700">
        {icon(percentage)}
        {label}
      </div>
      <div className={clsx('border-l pl-1', txtColor(percentage), borderColor(percentage))}>
        {value}
      </div>
    </div>
  );
}

export function campaignLineItemsPlanStatusTooltipValue({ data }: CampaignsTooltipProps<number>) {
  if (!data) return null;
  const priceAllocationPct = calcPriceAllocationPct(data);
  if (isNil(priceAllocationPct)) return [{ label: 'Pending campaign budget - Zoho' }];

  return [
    {
      label: 'Price allocation',
      percentage: priceAllocationPct,
      value: pctAllocatedLabel(priceAllocationPct)
    }
  ];
}

export function campaignMediaPlanStatusTooltipValue(props: CampaignsTooltipProps<number>) {
  if (!props.data) return null;
  const lineItemsWithPlatformsPct = calcLineItemsWithPlatformsPct(props.data);
  const lineItemsWithMediaPlansPct = calcLineItemsWithMediaPlansPct(props.data);
  const mediaPlanWithPlatformBuyPct = calcMediaPlanWithPlatformBuyPct(props.data);

  if (
    isNil(lineItemsWithPlatformsPct) ||
    isNil(lineItemsWithMediaPlansPct) ||
    isNil(mediaPlanWithPlatformBuyPct)
  ) {
    return [{ label: 'Pending line item plan' }];
  }

  return [
    {
      label: 'Line items w/ platforms',
      percentage: lineItemsWithPlatformsPct,
      value: countAllocatedLabel(lineItemsWithPlatformsPct)
    },
    {
      label: 'Line items w/ media plans',
      percentage: lineItemsWithMediaPlansPct,
      value: countAllocatedLabel(lineItemsWithMediaPlansPct)
    },
    {
      label: 'Media plans w/ platform buys',
      percentage: mediaPlanWithPlatformBuyPct,
      value: countAllocatedLabel(mediaPlanWithPlatformBuyPct)
    }
  ];
}

export function calcPriceAllocationPct(row: CampaignTableRow) {
  return row.price > 0 ? calcProportion(row.line_item_price_allocated, row.price) : undefined;
}

export function calcLineItemsWithPlatformsPct(row: CampaignTableRow) {
  return row.line_items_count > 0
    ? calcProportion(row.line_items_with_platforms_count, row.line_items_count)
    : undefined;
}

export function calcLineItemsWithMediaPlansPct(row: CampaignTableRow) {
  return row.line_items_count > 0
    ? calcProportion(row.line_items_with_media_plans_count, row.line_items_count)
    : undefined;
}

export function calcMediaPlanWithPlatformBuyPct(row: CampaignTableRow) {
  return row.total_media_plans_count > 0
    ? calcProportion(row.total_media_plans_linked, row.total_media_plans_count)
    : undefined;
}

function icon(percentage: number | undefined) {
  return percentage === undefined ? (
    <MinusInCircle />
  ) : percentage === 100 ? (
    <CheckInCircle />
  ) : (
    <Circle />
  );
}

function bgColor(pct: number | undefined) {
  return pct === undefined ? 'bg-gray-100' : pct === 100 ? 'bg-lime-100' : 'bg-yellow-50';
}

function txtColor(pct: number | undefined) {
  return pct === undefined ? null : pct === 100 ? 'text-lime-700' : 'text-yellow-700';
}

function borderColor(pct: number | undefined) {
  return pct === undefined ? null : pct === 100 ? 'border-l-lime-400' : 'bg-l-yellow-200';
}
